import React from 'react';
import { Clock, Package, Truck, CheckCircle, XCircle } from 'lucide-react';

/**
 * Horizontal timeline that shows the progression of an order across its lifecycle.
 *
 * Props:
 *   status: 'pending' | 'shipped' | 'delivered' | 'cancelled'
 *   history: optional array of { status, at } entries to display the actual dates
 */
const STEPS = [
  { key: 'pending', label: 'Confirmée', icon: Clock },
  { key: 'preparing', label: 'En préparation', icon: Package },
  { key: 'shipped', label: 'Expédiée', icon: Truck },
  { key: 'delivered', label: 'Livrée', icon: CheckCircle },
];

const findAt = (history, key) => {
  if (!Array.isArray(history)) return null;
  // shipped also acts as the "preparing" milestone if no explicit "preparing" event exists
  const entry = history.find((h) => h?.status === key);
  return entry?.at || null;
};

const fmt = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString('fr-DZ', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return null; }
};

const OrderTimeline = ({ status, history = [], createdAt }) => {
  if (status === 'cancelled') {
    const cancelAt = findAt(history, 'cancelled') || null;
    return (
      <div className="flex items-center gap-2.5 p-3 rounded-xl bg-[#F8E5D9] text-[#C75B27] text-sm" data-testid="order-timeline-cancelled">
        <XCircle className="w-4 h-4" />
        <span className="font-medium">Commande annulée</span>
        {cancelAt && <span className="text-xs text-[#A6532A]">— {fmt(cancelAt)}</span>}
      </div>
    );
  }

  // Map current status to the highest reached step (0..3)
  const currentIndex = status === 'delivered'
    ? 3
    : status === 'shipped'
      ? 2
      : 0; // pending -> first step

  return (
    <div className="pt-3" data-testid="order-timeline">
      <div className="relative flex items-start justify-between">
        {/* Progress bar background */}
        <div className="absolute top-4 left-0 right-0 h-0.5 bg-[#E8E0D0]" aria-hidden />
        {/* Progress bar fill */}
        <div
          className="absolute top-4 left-0 h-0.5 bg-[#1F4D3F] transition-all duration-500"
          style={{ width: `${(currentIndex / (STEPS.length - 1)) * 100}%` }}
          aria-hidden
        />

        {STEPS.map((step, idx) => {
          const Icon = step.icon;
          const reached = idx <= currentIndex;
          // Find the timestamp for this step
          let at = null;
          if (step.key === 'pending') at = findAt(history, 'pending') || createdAt;
          else if (step.key === 'preparing') at = idx <= currentIndex && idx > 0 ? findAt(history, 'shipped') : null;
          else at = findAt(history, step.key);

          return (
            <div
              key={step.key}
              className="relative z-10 flex flex-col items-center text-center flex-1 min-w-0"
              data-testid={`order-step-${step.key}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition ${
                  reached
                    ? 'bg-[#1F4D3F] border-[#1F4D3F] text-white'
                    : 'bg-white border-[#E8E0D0] text-[#C9BFAE]'
                }`}
              >
                <Icon className="w-4 h-4" />
              </div>
              <div className={`mt-2 text-[11px] font-medium px-1 ${reached ? 'text-[#1F4D3F]' : 'text-[#C9BFAE]'}`}>
                {step.label}
              </div>
              {reached && at && (
                <div className="text-[10px] text-[#7A6F5C] mt-0.5 truncate max-w-[90px]">
                  {fmt(at)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderTimeline;
